import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import { PaginatedBundleSchema } from '../domain/schema';

const QuestionnaireResponseStatusSchema = z.enum([
  'in-progress',
  'completed',
  'amended',
  'entered-in-error',
  'stopped',
]);

const AnswerSchema: z.ZodType<fhir4.QuestionnaireResponseItemAnswer> = z.object(
  {
    valueBoolean: z.boolean().optional(),
    valueDate: z.string().optional(),
    valueString: z.string().optional(),
    valueInteger: z.number().optional(),
    item: z.lazy(() => z.array(baseQuestionResponseAnswerSchema).optional()),
  },
);

export type Answer = z.infer<typeof AnswerSchema>;

const baseQuestionResponseAnswerSchema = z.object({
  linkId: z.string(),
  text: z.string().optional(),
  answer: z.array(AnswerSchema).optional(),
});

export type QuestionResponseAnswer = z.infer<
  typeof baseQuestionResponseAnswerSchema
> & {
  section?: QuestionResponseAnswer[];
};

export const QuestionResponseAnswerSchema: z.ZodType<QuestionResponseAnswer> =
  baseQuestionResponseAnswerSchema.extend({
    section: z.lazy(() => z.array(QuestionResponseAnswerSchema).optional()),
  });

const UpdateSchema = z.union([z.string(), z.number(), z.boolean(), z.date()]);
export type AnswerUpdate = z.infer<typeof UpdateSchema>;

const UpdateAnswerSchema = z.object({
  linkId: z.string(),
  answer: z.union([UpdateSchema, z.array(UpdateSchema)]),
});

export const QuestionResponseUpdateAnswerSchema = z.object({
  id: UUIDSchema,
  answers: z.array(UpdateAnswerSchema).min(1),
});

export const QuestionnaireResponseSchema = z.object({
  id: UUIDSchema,
  questionnaire: z.string().url(),
  careplan: DisplayReferenceSchema,
  admission: DisplayReferenceSchema,
  patient: DisplayReferenceSchema,
  status: QuestionnaireResponseStatusSchema,
  section: z.array(QuestionResponseAnswerSchema).optional(),
});

export const QuestionnaireResponseSearchParamsSchema = z.object({
  questionnaire: z.string().url().optional(),
  identifier: z.string().optional(),
  subject: UUIDSchema.optional(),
});

export const QuestionnaireResponseGetAllResponseSchema =
  PaginatedBundleSchema.extend({
    questionnaireResponses: QuestionnaireResponseSchema.array(),
  });

export type QuestionnaireResponse = z.infer<typeof QuestionnaireResponseSchema>;
export type QuestionResponseUpdate = z.infer<
  typeof QuestionResponseUpdateAnswerSchema
>;
export type UpdateAnswer = z.infer<typeof UpdateAnswerSchema>;

export type QuestionResponse = z.infer<typeof QuestionnaireResponseSchema>;
export type QuestionnaireResponseSearchParams = z.infer<
  typeof QuestionnaireResponseSearchParamsSchema
>;
export type QuestionnaireResponseGetAllResponse = z.infer<
  typeof QuestionnaireResponseGetAllResponseSchema
>;
